import 'react-toastify/dist/ReactToastify.min.css';

import { useMsal } from '@azure/msal-react';
import SystemLogo from '@shared/assets/svg/system-logo-light.svg?react';
import { msalAuthScopes } from '@shared/auth/config/msalAuthScope/web';
import { claimTransientSignInRetry } from '@shared/auth/transientSignInRetry';
import { LoginInitBar } from '@shared/components/LoginInitBar.tsx';
import { useProvisioningLockout } from '@shared/hooks/useProvisioningLockout.ts';
import { GlobalMessages } from '@shared/localization/messages';
import type { ReactNode } from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';

import { axiosEventBus } from '@/api/eventBus';
import { useLogin } from '@/api/hooks/useLogin';
import { Button } from '@/components/atoms/Button';
import { useMessages } from '@/hooks/messages';
import { reportEvent } from '@/services/crashReporting';

import { useAuth } from '../auth';

type IInitializationContext = {
  disableInitializationScreen: () => void;
};

const initContext: IInitializationContext = {
  disableInitializationScreen: () => {},
};

const InitializationContext = createContext(initContext);

/**
 * TODO: optimize/simplify:
 * - dont render children until conditions are met
 * - try to use tanstack router
 * - place loading splash screen outside of react - to show splashscreen before react app is loaded
 */
export const InitializationProvider = ({ children }: { children: ReactNode }) => {
  const [showSplashScreen, setShowSplashScreen] = useState<boolean>(true);
  const globalMessages = useMessages(GlobalMessages);
  const { tenant, user, authError, logout } = useAuth();
  const { inProgress, instance } = useMsal();
  const { login } = useLogin();
  const provisioningLockout = useProvisioningLockout(
    axiosEventBus.subscribe,
    axiosEventBus.unsubscribe,
  );

  useEffect(() => {
    let splashTimerId: ReturnType<typeof setTimeout> | undefined;

    if (tenant && user && inProgress === 'none') {
      splashTimerId = setTimeout(() => {
        setShowSplashScreen(false);
      }, 1000);
    }

    // EWM-1030: once sign-in has actually failed, re-arming this timer just redirects to B2C
    // again on every render - the loop the STAGE reporter saw as a spinner that never resolved.
    // Stop bouncing and let the error screen below offer a deliberate retry.
    // EWM-1066: a provisioning lockout is the same trap with no retry worth offering - MSAL holds
    // a perfectly valid token, so the forceLogin timer would silently re-run the failing bootstrap
    // every 5s. Stop here and let the screen below explain it and offer sign-out.
    if (authError || provisioningLockout) {
      return () => {
        clearTimeout(splashTimerId);
      };
    }

    const forceLoginTimerId = setTimeout(() => {
      if (inProgress === 'none' && !instance.getActiveAccount()) {
        // EWM-966: this timer is the app's only auth bootstrap after a hard reload, so it fires
        // on every reload - and a redirect reloads the SPA again, discarding whatever the user
        // had in progress. It cost the estimation wizard its state mid-flight and turned five
        // E2E specs red on build 20260819.11. A silent acquire reaches the same end state
        // without navigating: it emits ACQUIRE_TOKEN_SUCCESS even on a cache hit
        // (msal-browser StandardController), which is the event AuthProvider already handles to
        // set the active account and publish context.auth. Redirect only with no cached account.
        const [cachedAccount] = instance.getAllAccounts();

        if (cachedAccount) {
          void instance
            .acquireTokenSilent({ account: cachedAccount, scopes: msalAuthScopes })
            .catch(() => login())
            .catch(() => {
              throw new Error('Unexpected error during login');
            });
          return;
        }

        // EWM-500: was a console.log, so this diagnostic only ever reached the devtools of the
        // user it happened to. Sent to telemetry instead - an incompatible MSAL state forcing a
        // re-login is exactly the sort of thing worth seeing a rate for.
        // EWM-1051: was Dash0's sendEvent. Lands in App Insights `customEvents` instead, which is
        // where the rate is queryable alongside the rest of the client telemetry.
        reportEvent('msal.incompatible_state.force_login', { severity: 'WARN' });
        login().catch(() => {
          throw new Error('Unexpected error during login');
        });
      }
    }, 5000);

    return () => {
      clearTimeout(splashTimerId);
      clearTimeout(forceLoginTimerId);
    };
  }, [tenant, user, inProgress, login, instance, authError, provisioningLockout]);

  // EWM-1136: a transient MSAL failure (the B2C metadata fetch blipping) is not a rejected
  // credential, but it lands on the same terminal screen below and drops the sign-in. Give it
  // one automatic retry first. claimTransientSignInRetry bounds that to once per browser
  // session across the B2C redirect, so this cannot become the EWM-1030 loop.
  useEffect(() => {
    if (!authError || provisioningLockout) {
      return;
    }

    if (!claimTransientSignInRetry(authError)) {
      return;
    }

    reportEvent('msal.transient_signin_retry', { severity: 'WARN' });
    void login();
  }, [authError, provisioningLockout, login]);

  return (
    <InitializationContext.Provider
      value={useMemo<IInitializationContext>(
        () => ({
          disableInitializationScreen: () => {
            setShowSplashScreen(false);
          },
        }),
        [],
      )}
    >
      <div className='relative'>
        {children}

        {/* data-testid is load-bearing: the status page's hourly synthetic journey (EWM-925,
            status-page/src/journey.ts) treats this splash becoming hidden as the signal that
            initialisation finished. It cannot key off the text - that is localised - and it cannot
            key off <main>, which sits behind this overlay and is "visible" to Playwright throughout.
            Renaming it degrades that probe to `unknown`. */}
        <div
          data-testid='system-initializing'
          className={`dark:bg-c16 fixed top-0 left-0 z-[1000] flex h-screen w-screen items-center justify-center bg-white ${showSplashScreen || provisioningLockout ? '' : 'invisible'} `}
        >
          <div className='inline-block'>
            <SystemLogo className='w-96' />

            {provisioningLockout ? (
              // EWM-1066: checked before authError - it is the more specific failure and the only
              // one for which retrying is pointless, so it must not fall through to a retry button.
              <div className='mt-4 max-w-xl text-center'>
                <div data-testid='provisioning-blocked'>{globalMessages.provisioningBlocked()}</div>
                {provisioningLockout.traceId ? (
                  <div className='mt-2 text-xs opacity-70'>
                    Trace ID: {provisioningLockout.traceId}
                  </div>
                ) : null}
                <Button
                  className='mt-4'
                  variant='primary'
                  onClick={() => {
                    logout();
                  }}
                >
                  {globalMessages.signOut()}
                </Button>
              </div>
            ) : authError ? (
              <div className='mt-4 text-center'>
                <div data-testid='sign-in-failed'>{globalMessages.signInFailed()}</div>
                <Button
                  className='mt-4'
                  variant='primary'
                  onClick={() => {
                    void login();
                  }}
                >
                  {globalMessages.signInRetry()}
                </Button>
                {/* EWM-1165: a long-inactive early registration used to die on a silent 401 /
                    retired validate/code 404. Send them back through B2C where "Forgot your
                    password?" lives, and spell out the admin resend-invite path. */}
                <Button
                  className='mt-2'
                  variant='secondary'
                  data-testid='reset-password-or-resend-invite'
                  onClick={() => {
                    void login();
                  }}
                >
                  {globalMessages.resetPasswordOrResendInvite()}
                </Button>
              </div>
            ) : (
              <>
                <LoginInitBar bgColor='bg-c16' />

                <div>{globalMessages.initializatingSystem()}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </InitializationContext.Provider>
  );
};
